import {
  Alert,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
} from "@mui/material";
import { partition } from "lodash";
import * as React from "react";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { useUpdateLayout } from "@/shared/domain/layouts/hooks";
import {
  LayoutAccessibility,
  LayoutRecord,
} from "@/shared/domain/layouts/LayoutRecord";
import { CloseIcon } from "@/shared/foundation/icons/mui";

import { FolderNameInput } from "./FolderNameInput";
import { useBulkLayoutMutation } from "./useBulkLayoutMutation";

interface CreateFolderModalProps {
  /** Every layout the user could add to the new folder. */
  layouts: LayoutRecord[];
  existingFolders: string[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updated: LayoutRecord[]) => void;
}

export function CreateFolderModal({
  layouts,
  existingFolders,
  isOpen,
  onClose,
  onSuccess,
}: CreateFolderModalProps) {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="create-folder-dialog-title"
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      {/* Mount fresh each open so the name and selection reset between uses. */}
      {isOpen && (
        <CreateFolderForm
          layouts={layouts}
          existingFolders={existingFolders}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
    </Dialog>
  );
}

interface CreateFolderFormProps {
  layouts: LayoutRecord[];
  existingFolders: string[];
  onClose: () => void;
  onSuccess: (updated: LayoutRecord[]) => void;
}

function CreateFolderForm({
  layouts,
  existingFolders,
  onClose,
  onSuccess,
}: CreateFolderFormProps) {
  const [folder, setFolder] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const updateLayout = useUpdateLayout();
  const { run, isPending, errorMessage } = useBulkLayoutMutation(
    "Failed to create the folder. Please try again.",
  );

  const trimmed = folder.trim();
  const selectedLayouts = layouts.filter((layout) =>
    selectedIds.has(layout.layout_id),
  );
  // A layout folder only exists once it holds a layout, so require both a name and a pick.
  const canSubmit = trimmed !== "" && selectedLayouts.length > 0 && !isPending;

  const toggle = (layoutId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(layoutId)) {
        next.delete(layoutId);
      } else {
        next.add(layoutId);
      }
      return next;
    });
  };

  // Split the picker by accessibility, and only label the groups when both are present.
  const [personalLayouts, teamLayouts] = partition(
    layouts,
    (layout) => layout.accessibility === LayoutAccessibility.User,
  );
  const showGroupHeaders = personalLayouts.length > 0 && teamLayouts.length > 0;

  const renderLayoutItem = (layout: LayoutRecord) => (
    <ListItemButton
      key={layout.layout_id}
      onClick={() => toggle(layout.layout_id)}
      disabled={isPending}
      dense
    >
      <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
        <Checkbox
          edge="start"
          size="small"
          checked={selectedIds.has(layout.layout_id)}
          readOnly
          tabIndex={-1}
          disableRipple
          sx={{ p: 0 }}
          inputProps={{ "aria-label": `Add ${layout.name} to the folder` }}
        />
      </ListItemIcon>
      <ListItemText
        primary={layout.name}
        primaryTypographyProps={{ variant: "body2", noWrap: true }}
      />
    </ListItemButton>
  );

  const renderLayoutGroup = (label: string, groupLayouts: LayoutRecord[]) =>
    groupLayouts.length === 0 ? null : (
      <React.Fragment key={label}>
        {showGroupHeaders && (
          <ListSubheader disableSticky sx={{ lineHeight: 2, fontWeight: 600 }}>
            {label}
          </ListSubheader>
        )}
        {groupLayouts.map(renderLayoutItem)}
      </React.Fragment>
    );

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    run(
      selectedLayouts,
      (layout) =>
        updateLayout.mutateAsync({
          layoutId: layout.layout_id,
          updates: { folder: trimmed },
        }),
      () =>
        onSuccess(
          selectedLayouts.map((layout) => ({ ...layout, folder: trimmed })),
        ),
    );
  };

  return (
    <form onSubmit={onSubmit}>
      <DialogTitle id="create-folder-dialog-title" sx={{ pr: 6 }}>
        New folder
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body1">
            Name the folder and choose which layouts to add. The folder appears
            once it holds at least one layout.
          </Typography>

          <FolderNameInput
            value={folder}
            onChange={setFolder}
            existingFolders={existingFolders}
            label="Folder name"
            placeholder="e.g. Navigation"
            autoFocus
            disabled={isPending}
          />

          {layouts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              You need at least one saved layout to create a folder.
            </Typography>
          ) : (
            <List
              dense
              disablePadding
              sx={{
                maxHeight: 240,
                overflowY: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              {renderLayoutGroup("Personal", personalLayouts)}
              {renderLayoutGroup("Team", teamLayouts)}
            </List>
          )}

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <RobotoButton
          eventName="CreateLayoutFolderCanceled"
          onClick={onClose}
          type="button"
          disabled={isPending}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          eventName="CreateLayoutFolderClicked"
          eventProperties={() => ({
            folder: trimmed,
            count: selectedLayouts.length,
          })}
          type="submit"
          variant="contained"
          disabled={!canSubmit}
          endIcon={
            isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Create
        </RobotoButton>
      </DialogActions>
    </form>
  );
}
